import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();
  const [currentVideo, setCurrentVideo] = useState(0);

  const videos = [
    'https://customer-assets.emergentagent.com/job_e717eaca-d8b7-4a9d-a8e5-fc23f2442c14/artifacts/lbnzum0j_0%20Pepperoni%20Pizza%201280X720%20SMALL.mp4',
    'https://customer-assets.emergentagent.com/job_e717eaca-d8b7-4a9d-a8e5-fc23f2442c14/artifacts/434ge4e4_0%20Large%20Hamburger%201280X720%20SMALL.mp4',
    'https://customer-assets.emergentagent.com/job_e717eaca-d8b7-4a9d-a8e5-fc23f2442c14/artifacts/l1f9mmie_0%20Particles%20Microscopic%201280X720%20SMALL.mp4'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideo((prev) => (prev + 1) % videos.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [videos.length]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background with Slider */}
      <div className="absolute inset-0 w-full h-full">
        {videos.map((video, index) => (
          <video
            key={video}
            autoPlay
            muted
            loop
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentVideo ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <source src={video} type="video/mp4" />
          </video>
        ))}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        {/* Established Badge */}
        <div className="inline-block mb-8 animate-fade-in">
          <span className="px-4 py-2 bg-red-600 text-white text-xs font-bold tracking-widest rounded-full">
            {t('established')}
          </span>
        </div>

        {/* Main Title */}
        <h1 className="text-6xl md:text-8xl font-bold text-white mb-4 tracking-tight animate-slide-up">
          {t('heroTitle1')}
        </h1>
        <h2 className="text-6xl md:text-8xl font-bold text-red-500 mb-8 tracking-tight animate-slide-up" style={{ animationDelay: '0.1s' }}>
          {t('heroTitle2')}
        </h2>

        {/* Subtitle */}
        <div className="flex items-center justify-center gap-4 mb-12 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="h-px w-12 bg-red-500"></div>
          <p className="text-white text-lg md:text-xl max-w-2xl">
            {t('heroSubtitle')}
          </p>
          <div className="h-px w-12 bg-red-500"></div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <a
            href="https://order.site/milazzo"
            target="_blank"
            rel="noopener noreferrer"
            className="group px-8 py-4 bg-red-600 text-white font-medium tracking-wider hover:bg-red-700 transition-all duration-300 flex items-center gap-2 transform hover:scale-105"
          >
            {t('orderOnline')}
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>

        {/* Video Indicators */}
        <div className="flex items-center justify-center gap-2 mt-16 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          {videos.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentVideo(index)}
              className={`h-1 transition-all duration-300 ${
                index === currentVideo ? 'w-12 bg-red-500' : 'w-6 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/50 rounded-full animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;